import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import type {
  FormMetadata,
  ProcessedFormField,
} from "@/types/api/formSchema";
import taskerService from "../../../services/taskerService";

export type PriorStep = {
  formId: string;
  fields: ProcessedFormField[];
  metadata?: FormMetadata;
  data: Record<string, unknown>;
};

export type Row = { label: string; value: ReactNode };

export type UsePriorStepsResult = {
  data: PriorStep[] | undefined;
  isPending: boolean;
  isError: boolean;
  error: unknown;
};

export function usePriorSteps(
  taskId: string,
  taskTypeId: string | undefined,
): UsePriorStepsResult {
  // Distinct key from "reviewer-task-type" — sharing would corrupt the shape-mismatched cache entry.
  const orderedFormIdsQuery = useQuery({
    queryKey: ["reviewer-ordered-form-ids", taskTypeId],
    queryFn: async () => {
      const tt = await taskerService.getTaskType(
        taskTypeId as string,
        "reviewer",
      );
      const stepForms = (tt?.task_config?.step_forms ?? {}) as Record<
        string,
        string
      >;
      return Object.keys(stepForms)
        .sort((a, b) => {
          const na = Number(a.match(/STEP_(\d+)/)?.[1] ?? 0);
          const nb = Number(b.match(/STEP_(\d+)/)?.[1] ?? 0);
          return na - nb;
        })
        .map((state) => stepForms[state]);
    },
    enabled: Boolean(taskTypeId),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const priorFormIds = orderedFormIdsQuery.data ?? [];

  const innerQuery = useQuery({
    queryKey: ["reviewer-prior-steps", taskId, priorFormIds],
    queryFn: async (): Promise<PriorStep[]> => {
      return Promise.all(
        priorFormIds.map(async (fid) => {
          const [schema, rows] = await Promise.all([
            taskerService.getFormSchema(fid, taskId, "reviewer"),
            taskerService.searchFormData(taskId, fid, "reviewer"),
          ]);
          const latest = [...rows].sort((a, b) => {
            const ta = new Date((a.created_at as string) ?? 0).getTime();
            const tb = new Date((b.created_at as string) ?? 0).getTime();
            return tb - ta;
          })[0];
          return {
            formId: fid,
            fields: schema.fields,
            metadata: schema.metadata,
            data: (latest?.form_data ?? {}) as Record<string, unknown>,
          };
        }),
      );
    },
    enabled: Boolean(taskId && priorFormIds.length > 0),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: false,
  });

  // Collapse the two dependent queries into a single loading/error/data
  // view. `isInitialLoading` = isLoading && isFetching, which excludes disabled
  // queries — so the outer-errored and outer-succeeded-with-no-form-ids paths
  // don't look like an in-flight fetch forever.
  const isPending =
    orderedFormIdsQuery.isInitialLoading ||
    (orderedFormIdsQuery.isSuccess &&
      priorFormIds.length > 0 &&
      innerQuery.isInitialLoading);
  const isError = orderedFormIdsQuery.isError || innerQuery.isError;
  const data: PriorStep[] | undefined =
    orderedFormIdsQuery.isSuccess && priorFormIds.length === 0
      ? []
      : innerQuery.data;

  return {
    data,
    isPending,
    isError,
    error: orderedFormIdsQuery.error ?? innerQuery.error,
  };
}

